<template>
  <div class="campaign-picker">
    <label
      v-if="preferredIds.length"
      class="check"
    ><input
      v-model="searchAll"
      type="checkbox"
    >{{ words.searchOtherArticles }}</label>
    <label>{{ title }}<input
      v-model="search"
      :placeholder="words.pickerSearch"
      maxlength="100"
      @keydown.enter.prevent="find"
    ></label>
    <button
      type="button"
      :disabled="loading"
      @click="find"
    >
      {{ words.pick }}
    </button>
    <small v-if="kind === 'tags'">{{ words.tagSearchHint }}</small>
    <p
      v-if="value.length"
      class="campaign-picker-summary"
    >
      {{ words.selectedItems }}: {{ value.length }}
    </p>
    <div
      v-if="value.length"
      class="campaign-picker-selection"
      role="region"
      :aria-label="`${title} · ${words.selectedItems}`"
      tabindex="0"
    >
      <div
        v-for="id in value"
        :key="`selected-${id}`"
        class="campaign-picker-selected"
      >
        <span :dir="locale === 'en' ? 'ltr' : 'rtl'">{{ name(id) }}</span><button
          type="button"
          @click="remove(id)"
        >
          {{ words.remove }}
        </button>
      </div>
    </div>
    <small v-if="maximum > 1 && value.length >= maximum">{{ words.pickerLimit.replace('{count}', maximum) }}</small>
    <div
      v-if="searched"
      :key="resultsVersion"
      class="campaign-picker-results"
      role="region"
      :aria-label="`${title} · ${words.pickerResults}`"
      :aria-busy="loading ? 'true' : 'false'"
      tabindex="0"
    >
      <label
        v-for="option in visibleOptions"
        :key="`option-${option.id}`"
        class="check"
      ><input
        :type="maximum === 1 ? 'radio' : 'checkbox'"
        :name="maximum === 1 ? `picker-${kind}-${title}` : null"
        :checked="value.includes(option.id)"
        :disabled="loading || (maximum !== 1 && !value.includes(option.id) && value.length >= maximum)"
        @change="toggle(option)"
      ><span :dir="locale === 'en' ? 'ltr' : 'rtl'">{{ option.title }}</span></label>
      <p
        v-if="options.length && !visibleOptions.length"
        role="status"
      >
        {{ words.resultsSelected }}
      </p>
      <p
        v-if="searched && !loading && !options.length && !message"
        role="status"
      >
        {{ kind === 'tags' ? words.noTagOptions : words.noOptions }}
      </p>
    </div>
    <p
      v-if="loading"
      role="status"
    >
      {{ words.loading }}
    </p>
    <p
      v-if="message"
      role="status"
    >
      {{ message }}
    </p>
    <button
      v-if="failedPage"
      type="button"
      :disabled="loading"
      @click="retry"
    >
      {{ words.retryList }}
    </button>
    <small v-if="total > 200000">{{ words.pickerNarrow }}</small>
    <div
      v-if="pageQuery && page"
      class="campaign-picker-pages"
      :aria-label="words.pickerPages"
    >
      <button
        type="button"
        :disabled="loading || page <= 1"
        @click="loadPage(page - 1)"
      >
        {{ words.previous }}
      </button>
      <span role="status">{{ pageSummary }}</span>
      <button
        type="button"
        :disabled="loading || page >= pages || suggested"
        @click="loadPage(page + 1)"
      >
        {{ words.next }}
      </button>
    </div>
  </div>
</template>
<script>
import axios from '@/libs/axios'
import messages from './messages'
import { errorKey } from './model'

const pageSize = 20

export default {
  props: {
    value: { type: Array, default: () => [] }, kind: { type: String, required: true }, title: { type: String, required: true }, locale: { type: String, default: 'fa' }, maximum: { type: Number, default: 50 }, preferredIds: { type: Array, default: () => [] }, reportContext: Boolean,
  },
  data() {
    return {
      search: '', searchAll: false, options: [], selected: [], lookupState: {}, loading: false, message: '', sequence: 0, lookupSequence: 0, searched: false, page: 0, total: 0, pageQuery: null, failedPage: 0, suggested: false, resultsVersion: 0,
    }
  },
  computed: {
    visibleOptions() { return this.reportContext ? this.options : this.options.filter(option => !this.value.includes(option.id)) },
    words() { return messages[this.$i18n.locale] || messages.fa },
    endpoint() { return this.reportContext ? 'content-campaign-reports/options' : 'content-campaigns/options' },
    pages() { return Math.min(10000, Math.max(1, Math.ceil(this.total / pageSize))) },
    pageSummary() { return this.words.pickerPage.replace('{page}', this.page).replace('{pages}', this.pages) },
  },
  watch: {
    search() { this.clearResults() },
    searchAll() { this.clearResults() },
    preferredIds: { deep: true, handler() { this.clearResults() } },
    value: { deep: true, handler() { this.selected = this.selected.filter(item => this.value.includes(item.id)); this.hydrate() } },
    locale() { this.reset() },
    kind() { this.reset() },
    reportContext() { this.reset() },
  },
  created() { this.hydrate() },
  beforeDestroy() { this.sequence += 1; this.lookupSequence += 1 },
  methods: {
    name(id) { const found = this.selected.find(item => item.id === id); if (found) return found.title; if (this.lookupState[id] === 'failed') return this.words.lookupFailed; if (this.lookupState[id] === 'unavailable') return this.words.optionUnavailable; return this.words.loading },
    clearResults() {
      this.sequence += 1; this.loading = false; this.options = []; this.message = ''; this.searched = false; this.page = 0; this.total = 0; this.pageQuery = null; this.failedPage = 0; this.suggested = false
    },
    reset() { this.lookupSequence += 1; this.selected = []; this.lookupState = {}; this.clearResults(); this.hydrate() },
    rows(response) { if (!response.data || !Array.isArray(response.data.items)) throw new Error('INVALID_OPTIONS'); return response.data.items.filter(item => item && /^[a-f0-9]{24}$/i.test(item.id || '') && typeof item.title === 'string' && item.title.trim()) },
    remember(options) {
      const recovered = options.filter(item => this.value.includes(item.id)); this.selected = [...this.selected.filter(item => !recovered.some(option => option.id === item.id)), ...recovered]
    },
    remove(id) { this.selected = this.selected.filter(item => item.id !== id); this.$emit('input', this.value.filter(item => item !== id)) },
    toggle(option) { if (this.loading) return; if (this.value.includes(option.id)) { if (this.maximum !== 1) this.remove(option.id); return } if (this.maximum === 1) { this.selected = [option]; this.$emit('input', [option.id]) } else if (this.value.length < this.maximum) { this.selected = [...this.selected.filter(item => item.id !== option.id), option]; this.$emit('input', [...this.value, option.id]) } },
    async hydrate() {
      this.lookupSequence += 1; const sequence = this.lookupSequence; const ids = this.value.filter(id => !this.selected.some(item => item.id === id)).slice(0, 100)
      if (!ids.length) return
      ids.forEach(id => this.$set(this.lookupState, id, 'loading')); const batches = []; for (let index = 0; index < ids.length; index += 50) batches.push(ids.slice(index, index + 50))
      try {
        const responses = await Promise.all(batches.map(batch => axios.post(this.endpoint, {
          kind: this.kind, locale: this.locale, ids: batch, page: 1, limit: 50,
        }, { timeout: 10000 }))); if (sequence !== this.lookupSequence) return; const options = responses.flatMap(response => this.rows(response)).filter(item => this.value.includes(item.id)); this.selected = [...this.selected.filter(item => this.value.includes(item.id)), ...options]; ids.forEach(id => this.$set(this.lookupState, id, options.some(item => item.id === id) ? 'ready' : 'unavailable'))
      } catch (error) { if (sequence === this.lookupSequence) ids.forEach(id => this.$set(this.lookupState, id, 'failed')) }
    },
    async find() {
      if (this.loading) return
      this.clearResults(); this.searched = true
      const ids = !this.searchAll ? [...new Set(this.preferredIds)].slice(0, 100) : []
      if (!ids.length) {
        this.pageQuery = {
          kind: this.kind, locale: this.locale, search: this.search.trim(), endpoint: this.endpoint,
        }
        await this.loadPage(1)
        return
      }
      this.sequence += 1; const { sequence } = this; this.loading = true
      const search = this.search.trim().toLocaleLowerCase()
      try {
        const queries = Array.from({ length: Math.ceil(ids.length / 50) }, (_, index) => ({
          kind: this.kind, locale: this.locale, ids: ids.slice(index * 50, index * 50 + 50), page: 1, limit: 50,
        }))
        const responses = await Promise.all(queries.map(query => axios.post(this.endpoint, query, { timeout: 10000 })))
        if (sequence !== this.sequence) return
        this.options = responses.flatMap(response => this.rows(response)).filter(option => option.title.toLocaleLowerCase().includes(search))
        this.resultsVersion += 1; this.remember(this.options)
      } catch (error) { if (sequence === this.sequence) { this.message = this.words[errorKey(error)]; this.failedPage = 1 } } finally { if (sequence === this.sequence) this.loading = false }
    },
    async loadPage(page) {
      if (this.loading || !this.pageQuery || !Number.isInteger(page) || page < 1 || page > 10000 || (this.page && page > this.pages)) return
      this.sequence += 1; const { sequence } = this; const query = this.pageQuery
      this.loading = true; this.message = ''; this.failedPage = 0
      try {
        const response = await axios.post(query.endpoint, {
          kind: query.kind, locale: query.locale, search: query.search, page, limit: pageSize,
        }, { timeout: 10000 })
        if (sequence !== this.sequence) return
        const options = this.rows(response); const { data } = response
        if (!Number.isSafeInteger(data.total) || data.total < 0 || data.page !== page || data.limit !== pageSize || data.items.length > pageSize || page > Math.max(1, Math.ceil(data.total / pageSize))) throw new Error('INVALID_PAGINATION')
        if (new Set(options.map(option => option.id)).size !== options.length) throw new Error('DUPLICATE_OPTIONS')
        this.options = options; this.total = data.total; this.page = page; this.suggested = data.suggested === true
        if (this.suggested) this.message = this.words.tagSuggestions
        this.resultsVersion += 1; this.remember(options)
      } catch (error) { if (sequence === this.sequence) { this.message = this.words[errorKey(error)]; this.failedPage = page } } finally { if (sequence === this.sequence) this.loading = false }
    },
    retry() { if (this.pageQuery) return this.loadPage(this.failedPage); return this.find() },
  },
}
</script>
